import { create } from "zustand";
import { persist } from "zustand/middleware";

import { queryClient } from "@/services/query-client";
import type { TokenPair } from "@/meta/types";

const STORAGE_KEY = "mrm.session";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  signIn: (response: TokenPair) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      signIn: (response) =>
        set({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
        }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      signOut: () => {
        // Everything cached was fetched as the person leaving: their profile,
        // their permissions, their policy. The shell reads those through
        // `ensureQueryData`, which serves what it has while it is fresh — a
        // minute for the profile, five for the policy — so without this the
        // next person to sign in on this tab wears the last one's answers.
        queryClient.clear();
        set({ accessToken: null, refreshToken: null });
      },
    }),
    { name: STORAGE_KEY },
  ),
);

/** Read outside React — the axios interceptors need the current tokens. */
export const authSnapshot = () => useAuthStore.getState();

/**
 * The pair as it stands in storage, which is not always the pair this tab holds.
 *
 * `persist` writes on every change and reads once, at startup. Two tabs are
 * therefore two independent copies of one session, and a refresh token is
 * single-use: the moment one tab rotates, the other is holding a token the
 * server has already spent. Presenting it is what the server reads as theft.
 *
 * So the rotation path reads *here* rather than from its own state — storage
 * is the one copy both tabs write to, and it is current the instant the other
 * tab's `set` returns, with no event needing to have been delivered yet.
 *
 * Falls back to this tab's own state when storage cannot be read at all: a
 * private window, or a browser set to block site data. A tab that cannot see
 * the shared copy is no worse off than it was before this existed.
 */
export function persistedTokens(): Pick<AuthState, "accessToken" | "refreshToken"> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { accessToken: null, refreshToken: null };
    const parsed = JSON.parse(raw) as { state?: Partial<AuthState> };
    return {
      accessToken: parsed.state?.accessToken ?? null,
      refreshToken: parsed.state?.refreshToken ?? null,
    };
  } catch {
    const { accessToken, refreshToken } = useAuthStore.getState();
    return { accessToken, refreshToken };
  }
}

/**
 * Adopt a pair another tab minted, without re-minting one here.
 *
 * Distinct from `setTokens` only in intent — it is the name the rotation path
 * reads by — and in that it does nothing when the pair is already the one held,
 * so a `storage` event that tells a tab what it already knows does not push a
 * new state object through every subscriber.
 */
export function adoptTokens(accessToken: string | null, refreshToken: string | null): void {
  const state = useAuthStore.getState();
  if (state.accessToken === accessToken && state.refreshToken === refreshToken) return;
  if (!accessToken || !refreshToken) {
    // The other tab signed out, or its refresh was refused. Same ending here:
    // the route gates are subscribed, so the sign-in screen arrives with it.
    if (state.accessToken || state.refreshToken) state.signOut();
    return;
  }
  state.setTokens(accessToken, refreshToken);
}

/**
 * Keep every tab on the pair the newest rotation produced.
 *
 * Without this a background tab sits on the pair it had at sign-in for as long
 * as it is open. Its access token dies after fifteen minutes, its next request
 * is a 401, and the refresh it reaches for was spent by the foreground tab
 * hours ago — which the server answers by revoking every session for the
 * account. Opening a second tab was enough to sign someone out of both.
 *
 * `storage` fires only in the tabs that did *not* write, which is exactly the
 * set that needs telling.
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    const { accessToken, refreshToken } = persistedTokens();
    adoptTokens(accessToken, refreshToken);
  });
}
